import type { TeamBillingInfo } from '@overlayqa/shared';
import { hasActivePlan } from '@overlayqa/shared';

interface PollBillingOptions {
  /** Force a cache-bypassing billing read (POST /api/billing/refresh). */
  fetchFresh: () => Promise<TeamBillingInfo>;
  /** Max reads before giving up. Default 5. */
  maxAttempts?: number;
  /** Delay between reads. Default 2000ms. */
  delayMs?: number;
  /** Injectable sleep for tests. */
  sleep?: (ms: number) => Promise<void>;
  /** Called with each successful read so the UI can update as the plan flips. */
  onUpdate?: (info: TeamBillingInfo) => void;
}

/**
 * Has billing acknowledged a paid subscription? billing.service.ts exposes the
 * trial clock only for beta-sourced access; a Clerk or Stripe subscription with
 * leftover trial days receives null. The Team slug alone also grants trials and
 * cannot acknowledge checkout. One predicate for the poll and for the success
 * message, so the page never celebrates a state the poll would keep waiting on.
 */
export function isSubscriptionAcknowledged(info: TeamBillingInfo): boolean {
  return hasActivePlan(info.plan) && info.betaExpiresAt === null;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Force-refresh billing until a subscription is acknowledged, or attempts run out.
 *
 * Clerk's checkout drawer fires onSubscriptionComplete before its subscription
 * webhook propagates and before a Clerk force-read is guaranteed to reflect the
 * new plan. A single refresh can therefore still see a null plan or Team
 * access granted by an OverlayQA trial. Neither acknowledges the subscription.
 * Stop once a known plan has no trial clock; otherwise return the last read
 * after the retry budget, preserving the server's actual access state.
 * Transient read failures are swallowed and retried.
 */
export async function pollBillingUntilActive({
  fetchFresh,
  maxAttempts = 5,
  delayMs = 2000,
  sleep = defaultSleep,
  onUpdate,
}: PollBillingOptions): Promise<TeamBillingInfo | null> {
  let last: TeamBillingInfo | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const info = await fetchFresh();
      last = info;
      onUpdate?.(info);
      if (isSubscriptionAcknowledged(info)) return info;
    } catch {
      // Transient failure (network blip, Clerk hiccup). Keep polling; the
      // caller's normal refetch is the ultimate backstop.
    }

    if (attempt < maxAttempts - 1) {
      await sleep(delayMs);
    }
  }

  return last;
}
